import { MouseEventHandler, TouchEventHandler, useState, TouchEvent, Touch, MouseEvent } from 'react';
import { HtmlVector } from 'record-it-utils/misc/HtmlVector/HtmlVector';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { useEventCallback } from '../../hooks/useEventCallback';
import { useEventListener } from '../../hooks/useEventListener';
import { CalculatedTick } from './useCalculatedTickConfiguration';
export function useEventHandlers({
  value,
  allTicks,
  container,
  onValueChanged,
  onValueSelected
}: UseEventHandlersOptions): UseEventHandlersReturnValue {
  const [highlightedValueOverride, setHighlightedValueOverride] = useState<number | null>(null);
  const onMouseDown: MouseEventHandler<HTMLElement> = useEventCallback(event => {
    if (event.buttons === 1) {
      // only do something if mouse button 1 is pressed
      updateHighlightedValueOverride(event);
    }
  });
  const onMouseMove: MouseEventHandler<HTMLElement> = useEventCallback(event_0 => {
    if (event_0.buttons === 1) {
      // only do something if mouse button 1 is pressed
      updateHighlightedValueOverride(event_0);
    }
  });
  useEventListener(document.body, 'mouseup', () => {
    acceptHighlightedValueOveride();
  });
  const onTouchStart: TouchEventHandler<HTMLElement> = useEventCallback(event_1 => {
    const touch = getSingleTouch(event_1);
    if (touch) {
      updateHighlightedValueOverride(touch);
    } else {
      setHighlightedValueOverride(null);
    }
  });
  const onTouchMove: TouchEventHandler<HTMLElement> = useEventCallback(event_2 => {
    const touch_0 = getSingleTouch(event_2);
    if (touch_0) {
      updateHighlightedValueOverride(touch_0);
    }
  });
  const onTouchEnd: TouchEventHandler<HTMLElement> = useEventCallback(event_3 => {
    if (event_3.touches.length === 0) {
      event_3.preventDefault();
      acceptHighlightedValueOveride();
    }
  });
  const onTouchCancel: TouchEventHandler<HTMLElement> = useEventCallback(() => {
    setHighlightedValueOverride(null);
  });
  function getSingleTouch(event_4: TouchEvent<HTMLElement>): Touch | null {
    const firstTouch = event_4.touches[0];
    if (firstTouch && event_4.touches.length === 1) {
      return firstTouch;
    }
    return null;
  }
  function updateHighlightedValueOverride(eventOrTouch: MouseEvent | Touch): void {
    const position = getRelativePosition(eventOrTouch.clientX, eventOrTouch.clientY);
    const closestTick = findClosestSelectableTick(position);
    if (closestTick) {
      setHighlightedValueOverride(closestTick.value);
    }
  }
  function acceptHighlightedValueOveride(): void {
    if (highlightedValueOverride != null) {
      onValueSelected?.(highlightedValueOverride);
      if (highlightedValueOverride !== value) {
        onValueChanged?.(highlightedValueOverride);
      }
      setHighlightedValueOverride(null);
    }
  }
  function getRelativePosition(clientX: number, clientY: number): HtmlVector {
    assertNotNullOrUndefined(container, "can't calculate a position without a container");
    const boundingRect = container.getBoundingClientRect();
    const topLeftPosition = new HtmlVector(boundingRect.left, boundingRect.top);
    return new HtmlVector(clientX, clientY).substractVector(topLeftPosition);
  }
  function findClosestSelectableTick(position_0: HtmlVector): CalculatedTick | null {
    const threshold = 36;
    let closestTick_0: CalculatedTick | null = null;
    let closestDistance: number | null = null;
    const selectableTicks = allTicks.filter(t => t.selectable);
    for (const tick of selectableTicks) {
      const diffVector = position_0.substractVector(tick.position);
      const distance = diffVector.getLength();
      if (distance < threshold && (closestDistance == null || distance < closestDistance)) {
        closestTick_0 = tick;
        closestDistance = distance;
      }
    }
    return closestTick_0;
  }
  const valueToHighlight = highlightedValueOverride ?? value;
  return {
    highlightedTick: allTicks.find(t_0 => t_0.value === valueToHighlight) ?? null,
    containerProps: {
      onMouseMove,
      onMouseDown,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onTouchCancel
    }
  };
}
export type UseEventHandlersOptions = {
  container: HTMLElement | null;
  value: number;
  onValueChanged: ((value: number) => void) | undefined;
  onValueSelected: ((value: number) => void) | undefined;
  allTicks: Array<CalculatedTick>;
};
export type UseEventHandlersReturnValue = {
  highlightedTick: CalculatedTick | null;
  containerProps: {
    onMouseDown: MouseEventHandler<HTMLElement>;
    onMouseMove: MouseEventHandler<HTMLElement>;
    onTouchStart: TouchEventHandler<HTMLElement>;
    onTouchMove: TouchEventHandler<HTMLElement>;
    onTouchEnd: TouchEventHandler<HTMLElement>;
    onTouchCancel: TouchEventHandler<HTMLElement>;
  };
};